// =============================================================================
// loginName — the login-name convention for staff who have no email
// (docs/specs/no-mailbox-logins.md §4).
//
//   <firstname>.<surname>@<tenant-slug>.staff.invalid
//
// GoTrue needs a unique, email-SHAPED identifier per account, not a mailbox.
// `.invalid` is RFC 2606-reserved and can never route, so nothing is ever
// sent to it — which is the point. The tenant slug is in the address because
// GoTrue emails are unique platform-wide and two farms can each have a Sipho
// Dube. Pure; the edge functions carry the same domain constant in
// supabase/functions/_shared/authLinks.ts — change both together (D1).
// =============================================================================

export const STAFF_LOGIN_DOMAIN = 'staff.invalid'

/** The tenant label as it appears in the address: `[a-z0-9]` only, the same
 *  reduction deviceSignIn.ts applies to `@devices.invalid` identities. */
export function tenantSlugLabel(slug: string | null | undefined): string {
  return (slug ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '')
}

/** A regex for Postgres `~*` (PostgREST `imatch`) that matches every slug
 *  with the same staff-login label as `slug`: the label's characters in
 *  order, nothing but non-letters/digits around and between them. Lets a
 *  screen ask "does any business have this label?" in one bounded read, the
 *  question mig 20261037500000's tenants_login_label_key answers at INSERT.
 *  The label is [a-z0-9] only, so nothing needs escaping. Screen-side only:
 *  the edge functions do not pre-check the label, the index refuses at the
 *  INSERT and _shared/loginLabelClash.ts words that refusal. */
export function loginLabelSlugPattern(slug: string | null | undefined): string {
  const gap = '[^a-z0-9]*'
  return `^${gap}${tenantSlugLabel(slug).split('').join(gap)}${gap}$`
}

/** `Sipho Dube` → `sipho.dube`. Lower case, accents stripped, apostrophes
 *  dropped, hyphens kept, other punctuation removed, spaces → dots. */
export function loginNameLocalPart(fullName: string | null | undefined): string {
  return (fullName ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')      // combining marks left by NFKD
    .toLowerCase()
    .replace(/['’‘`]/g, '')               // D'Arcy → darcy
    .replace(/[\s.]+/g, '.')              // spaces (and existing dots) → one dot
    .replace(/[^a-z0-9.-]/g, '')          // everything else goes; hyphens stay
    .replace(/\.{2,}/g, '.')
    .replace(/^[.-]+|[.-]+$/g, '')
}

/** The suggested login name, or '' when either half is empty. */
export function suggestLoginName(fullName: string | null | undefined, tenantSlug: string | null | undefined): string {
  const local  = loginNameLocalPart(fullName)
  const tenant = tenantSlugLabel(tenantSlug)
  if (!local || !tenant) return ''
  return `${local}@${tenant}.${STAFF_LOGIN_DOMAIN}`
}

/** True when `email` is a staff login name — `name@<tenant>.staff.invalid`
 *  exactly (one tenant label, nothing more). Device identities on
 *  `@devices.invalid` are the OTHER synthetic family and are not. */
export function isNoMailboxAddress(email: string | null | undefined): boolean {
  const e = (email ?? '').trim().toLowerCase()
  const at = e.lastIndexOf('@')
  if (at <= 0) return false
  const domain = e.slice(at + 1)
  if (!domain.endsWith(`.${STAFF_LOGIN_DOMAIN}`)) return false
  const tenant = domain.slice(0, -(STAFF_LOGIN_DOMAIN.length + 1))
  return tenant.length > 0 && !tenant.includes('.')
}
